/**
 * Rich-text helpers for clause bodies. Clause `body` is stored as HTML (authored in the admin
 * TipTap editor) and rendered in both the admin view and the franchisee terms card / snapshot.
 *
 * Security: HTML is ALWAYS sanitized on render via {@link sanitizeHtml} (one canonical guard,
 * defence-in-depth even though authoring is admin-only). The allowlist is deliberately narrow —
 * formatting + links only, no images/scripts/styles (see the RTE scope decision).
 */
import DOMPurify from 'dompurify';

/** Tags the editor can produce and we are willing to render. Everything else is stripped. */
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'blockquote', 'mark'];
// `title` powers the market-value hover hint on <mark> (see lib/variables applyVariablesMarked).
// `start`/`type` are TipTap's OrderedList numbering attrs (e.g. a split/continued list starting
// at 2, or a/i/A/I styles) — numeric/enum only, never URL-bearing.
const ALLOWED_ATTR = ['href', 'target', 'rel', 'title', 'start', 'type'];
/**
 * Attrs DOMPurify should NOT run through our narrow `ALLOWED_URI_REGEXP` (which only recognizes
 * `https?:`/`mailto:`/`tel:`/`#` — unlike DOMPurify's own default URI check, it has no fallback for
 * plain non-URL values like "3" or "a"). Without this, DOMPurify still validates every allowed
 * attribute's *value* against `ALLOWED_URI_REGEXP` unless the name is known non-URI, stripping
 * `start`/`type` even though they were allowed by name.
 */
const ADD_URI_SAFE_ATTR = ['start', 'type'];
/** Only safe link schemes — blocks `javascript:` and other script-bearing URIs. */
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|tel:|#)/i;

/**
 * True if a URL is safe to use as an `href` (same allowlist DOMPurify enforces on sanitized HTML).
 * Use this anywhere a URL is rendered into an anchor OUTSIDE sanitizeHtml — e.g. a link surfaced
 * from parsed clause text — so those paths can't reintroduce a `javascript:` link. Leading whitespace
 * is trimmed to match browser URL parsing. An unresolved `{{token}}` is not an allowed scheme, so it
 * is (correctly) treated as unsafe and rendered inert.
 */
export function isAllowedUrl(url: string): boolean {
  return ALLOWED_URI_REGEXP.test(url.trim());
}

let hookInstalled = false;
/** Force every surviving link to open safely (new tab, no opener) regardless of authored attrs. */
function installLinkHook(): void {
  if (hookInstalled) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  hookInstalled = true;
}

/** Sanitize authored clause HTML down to the formatting allowlist. Safe to call on every render. */
export function sanitizeHtml(html: string): string {
  installLinkHook();
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP, ADD_URI_SAFE_ATTR });
}

/**
 * True when rich-text HTML carries no visible content (empty paragraphs / whitespace only).
 * Pure (regex, no DOM) so it can gate "Save" without a browser — the editor emits `<p></p>` when empty.
 */
export function isRichTextEmpty(html: string): boolean {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

/**
 * Flatten clause HTML to plain text with block boundaries preserved as newlines. Pure (regex,
 * no DOM) so it can feed the version diff without a browser. Not for rendering — for comparison.
 *
 * Link targets are inlined as `text (href)` so the diff can see when a URL changes — including a
 * link becoming dynamic (a literal `https://…` href replaced by a `{{variable}}` token).
 */
export function htmlToText(html: string): string {
  return html
    .replace(/<a\b[^>]*\bhref=["']([^"']*)["'][^>]*>(.*?)<\/a>/gis, '$2 ($1)')
    .replace(/<(?:br|\/p|\/li|\/h2|\/h3|\/div|\/blockquote|\/ul|\/ol)\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** Tailwind child-selectors that style sanitized clause HTML consistently wherever it renders. */
export const RICH_TEXT_PROSE =
  '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 ' +
  '[&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic [&_u]:underline [&_s]:line-through ' +
  '[&_a]:text-byte-purple [&_a]:underline [&_a]:break-words ' +
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 ' +
  '[&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border-subtle [&_blockquote]:pl-3 [&_blockquote]:text-graphite-500 ' +
  '[&_mark]:rounded [&_mark]:bg-byte-yellow/15 [&_mark]:px-0.5 [&_mark]:text-inherit [&_mark]:ring-1 [&_mark]:ring-inset [&_mark]:ring-byte-yellow/30';
